import { create, StateCreator } from 'zustand';
import { IntakePhotoInfo, PhotoCoords, Site, Upload, UploadOverview } from '../types';
import { UploadType } from '../consts/uploadType';
import { FileWithPath } from 'react-dropzone';
import { devtools } from 'zustand/middleware';
import { GcpParseResult } from '../components/main_container/missions/openMission/gcpRequestUpload/gcpRequestUploadFiles/parseGcpCsv';

// Important: We do not use immer due to performance and memory hits here and instead mutate objects.
// `upload` objects always gets replaced, but we mutate objects inside of it,
// so do not use useMemo on anything inside `upload`. And always replace the upload in setUpload.

// For self-serviced pilots keys are 'customerId-siteId-uploadType'
// For TA pilots keys are `scheduleId-uploadType`

export interface QueuedIntakeFileInfo extends IntakePhotoInfo {
  file: FileWithPath;
  uploadedSize: number;
  isBeingValidated: boolean;
  isBeingUploaded: boolean;
}

export interface UploadData {
  overview: UploadOverview;
  upload?: Upload;
  queue: QueuedIntakeFileInfo[];
  meta: {
    abortController: AbortController;
    type: UploadType;
    site: Site;
    restoringUpload: boolean;
    error?: string;
    timezone?: string; // This is set when user drops files by querying the central point of the site against the backend
    currentBatch: number; // Drones can generate repeating names, so we have to prefix file picker results with a batch number
    isPreviouslyCompleted: boolean; // To avoid showing old completed uploads in the overall progress bar
    uploadSize?: number; // Cached upload size that we calculate during adding files and restoring uploads, needed for performance optimization
    uploadedSize?: number;
  };
}

export interface GeorefData {
  meta: {
    name: string;
    size: number;
    uploadedSize: number;
    isRegistrationRequestSent: boolean;
    abortController: AbortController;
    georefUploadPath: string; // e.g. "c1caad6a-4df3-48e0-9e0d-15b58c434bc4/georef/georef.txt"
    error?: string;
  };
  georef: { [name: string]: PhotoCoords | undefined } | undefined;
}

export enum RegistrationRequestType {
  dateConfirmation,
  uploadRegistration,
  batchRegistration,
  ppkGeorefRegistration,
}

export interface RegistrationRequest {
  type: RegistrationRequestType;
  fileHashes?: string[];
  filesSizeTotal?: number;
  abortController: AbortController;
}

export interface GcpRequestUploadFile {
  file: FileWithPath;
  uploadedSize: number;
  abortController: AbortController;
  promise: Promise<string>;
  isDone: boolean;
  uploadedUrl?: string;
  parseResult?: GcpParseResult | null; // null = not a parseable type; object = parsed (may have .error)
}

export interface GcpRequestData {
  airtableRecordId: string | null;
  measurements: string[];
  photos: string[];
  comment: string;
  gcpServiceLaborTimeInHours: number | undefined;
  autoGcpServiceLaborTimeInHours?: number;
  gcpCount?: number;
  // Parse results for measurement files that were already saved on the backend when this GCP
  // request was restored (as opposed to `measurementUploads`, which only covers files picked
  // in this session). undefined until the restore fetch completes; entries may be null on failure.
  restoredMeasurementParseResults?: Array<GcpParseResult | null>;
  measurementUploads: GcpRequestUploadFile[];
  photoUploads: GcpRequestUploadFile[];
  meta: {
    isEditMode: boolean;
    isSubmitting: boolean;
    prevMeasurements: string[];
    prevPhotos: string[];
    prevComment: string;
    prevGcpServiceLaborTimeInHours: number | undefined;
    areServiceHoursConfirmed?: boolean;
    prevMeasurementUploads: GcpRequestUploadFile[];
    prevPhotoUploads: GcpRequestUploadFile[];
  };
}

export interface UploadStoreState {
  // Current upload state + file upload queue
  uploads: {
    [key: string]: UploadData;
  };
  getUpload: (
    isPano: boolean,
    customerId: string,
    siteId: string,
    scheduleId: string | undefined,
  ) => UploadData | undefined;
  setUpload: (
    isPano: boolean,
    customerId: string,
    siteId: string,
    scheduleId: string | undefined,
    upload: UploadData | undefined,
  ) => void;

  // Registration requests: date confirmation, upload registration, batch registration
  registrationRequests: {
    [key: string]: Map<Promise<unknown>, RegistrationRequest>;
  };
  getRegistrationRequests: (
    isPano: boolean,
    customerId: string,
    siteId: string,
    scheduleId: string | undefined,
  ) => Map<Promise<unknown>, RegistrationRequest> | undefined;
  addRegistrationRequest: (
    isPano: boolean,
    customerId: string,
    siteId: string,
    scheduleId: string | undefined,
    promise: Promise<unknown>,
    registrationRequest: RegistrationRequest,
  ) => void;
  removeRegistrationRequest: (
    isPano: boolean,
    customerId: string,
    siteId: string,
    scheduleId: string | undefined,
    promise: Promise<unknown>,
  ) => void;
  clearRegistrationRequests: (
    isPano: boolean,
    customerId: string,
    siteId: string,
    scheduleId: string | undefined,
  ) => void;

  // Georefs for PPK scans
  georefs: {
    [key: string]: GeorefData;
  };
  getGeoref: (customerId: string, siteId: string, scheduleId: string | undefined) => GeorefData | undefined;
  setGeoref: (
    customerId: string,
    siteId: string,
    scheduleId: string | undefined,
    georef: GeorefData | undefined,
  ) => void;

  // Dates for unscheduled uploads (through "All projects")
  projectDates: {
    [key: string]: string;
  };
  getProjectDate: (
    isPano: boolean,
    customerId: string,
    siteId: string,
    scheduleId: string | undefined,
  ) => string | undefined;
  setProjectDate: (
    isPano: boolean,
    customerId: string,
    siteId: string,
    scheduleId: string | undefined,
    date: string | undefined,
  ) => void;

  // GCP Requests
  gcpRequests: {
    [key: string]: GcpRequestData;
  };
  getGcpRequest: (customerId: string, siteId: string) => GcpRequestData | undefined;
  setGcpRequest: (customerId: string, siteId: string, gcpRequest: GcpRequestData | undefined) => void;
  hasGcpFormChanges: (customerId: string, siteId: string) => boolean;
  hasGcpUploadingFiles: (customerId: string, siteId: string) => boolean;

  // Speed measurement
  bytesPerSecond: number | undefined;
  setBytesPerSecond: (bytesPerSecond: number | undefined) => void;

  // Time it takes Pilot API to register a file (starting with InitialFileRegistrationTimeMs and then adjusting)
  fileRegistrationTimeMs: number;
  setFileRegistrationTimeMs: (time: number) => void;
}

const getFlightUploadKey = (isPano: boolean, customerId: string, siteId: string, scheduleId: string | undefined) => {
  const type = isPano ? 'pano' : 'scan';
  if (scheduleId) {
    return `${scheduleId}-${type}`;
  }
  return `${customerId}-${siteId}-${type}`;
};

const getGcpUploadKey = (customerId: string, siteId: string) => {
  return `${customerId}-${siteId}`;
};

const createUploadStoreImpl: StateCreator<UploadStoreState, [['zustand/devtools', never]], []> = (set, get) => ({
  uploads: {},
  getUpload: (isPano, customerId, siteId, scheduleId) => {
    const key = getFlightUploadKey(isPano, customerId, siteId, scheduleId);
    return get().uploads[key];
  },
  setUpload: (isPano, customerId, siteId, scheduleId, upload) => {
    const key = getFlightUploadKey(isPano, customerId, siteId, scheduleId);
    set((state) => {
      const uploads = { ...state.uploads };
      if (!upload) {
        delete uploads[key];
      } else {
        uploads[key] = upload;
      }
      return {
        uploads,
      };
    });
  },

  registrationRequests: {},
  getRegistrationRequests: (isPano, customerId, siteId, scheduleId) => {
    const key = getFlightUploadKey(isPano, customerId, siteId, scheduleId);
    return get().registrationRequests[key];
  },
  addRegistrationRequest: (isPano, customerId, siteId, scheduleId, promise, registrationRequest) => {
    const key = getFlightUploadKey(isPano, customerId, siteId, scheduleId);
    set((state) => {
      const registrationRequests = { ...state.registrationRequests };
      if (!registrationRequests[key]) {
        registrationRequests[key] = new Map();
      }
      registrationRequests[key].set(promise, registrationRequest);
      return {
        registrationRequests,
      };
    });
  },
  removeRegistrationRequest: (isPano, customerId, siteId, scheduleId, promise) => {
    const key = getFlightUploadKey(isPano, customerId, siteId, scheduleId);
    set((state) => {
      const registrationRequests = { ...state.registrationRequests };
      if (registrationRequests[key]) {
        registrationRequests[key].delete(promise);
      }
      return {
        registrationRequests,
      };
    });
  },
  clearRegistrationRequests: (isPano, customerId, siteId, scheduleId) => {
    const key = getFlightUploadKey(isPano, customerId, siteId, scheduleId);
    set((state) => {
      const registrationRequests = { ...state.registrationRequests };
      delete registrationRequests[key];
      return {
        registrationRequests,
      };
    });
  },

  georefs: {},
  getGeoref: (customerId, siteId, scheduleId) => {
    const key = getFlightUploadKey(false, customerId, siteId, scheduleId);
    return get().georefs[key];
  },
  setGeoref: (customerId, siteId, scheduleId, georef) => {
    const key = getFlightUploadKey(false, customerId, siteId, scheduleId);
    set((state) => {
      const georefs = { ...state.georefs };
      if (!georef) {
        delete georefs[key];
      } else {
        georefs[key] = georef;
      }
      return {
        georefs,
      };
    });
  },

  projectDates: {},
  getProjectDate: (isPano, customerId, siteId, scheduleId) => {
    const key = getFlightUploadKey(isPano, customerId, siteId, scheduleId);
    return get().projectDates[key];
  },
  setProjectDate: (isPano, customerId, siteId, scheduleId, date) => {
    const key = getFlightUploadKey(isPano, customerId, siteId, scheduleId);
    set((state) => {
      const projectDates = { ...state.projectDates };
      if (!date) {
        delete projectDates[key];
      } else {
        projectDates[key] = date;
      }
      return {
        projectDates,
      };
    });
  },

  gcpRequests: {},
  getGcpRequest: (customerId, siteId) => {
    const key = getGcpUploadKey(customerId, siteId);
    return get().gcpRequests[key];
  },
  setGcpRequest: (customerId, siteId, gcpRequest) => {
    const key = getGcpUploadKey(customerId, siteId);
    set((state) => {
      const gcpRequests = { ...state.gcpRequests };
      if (!gcpRequest) {
        delete gcpRequests[key];
      } else {
        gcpRequests[key] = gcpRequest;
      }
      return {
        gcpRequests,
      };
    });
  },
  /**
   * Checks if the current GCP form has unsaved changes compared to its last submitted state.
   * Compares measurements, photos, and comment fields with their previous values.
   *
   * @param customerId - ID of the customer associated with the GCP request
   * @param siteId - ID of the site associated with the GCP request
   * @returns `true` if any field has changed, otherwise `false`
   */
  hasGcpFormChanges: (customerId: string, siteId: string): boolean => {
    const req = get().getGcpRequest(customerId, siteId);
    if (!req) return false;

    return (
      req.measurements.toString() !== req.meta.prevMeasurements.toString() ||
      req.photos.toString() !== req.meta.prevPhotos.toString() ||
      req.comment !== req.meta.prevComment ||
      req.gcpServiceLaborTimeInHours !== req.meta.prevGcpServiceLaborTimeInHours
    );
  },
  /**
   * Determines whether any GCP file uploads are still in progress.
   * Checks both measurement and photo uploads for incomplete status.
   *
   * @param customerId - ID of the customer associated with the GCP request
   * @param siteId - ID of the site associated with the GCP request
   * @returns `true` if any files are still uploading, otherwise `false`
   */
  hasGcpUploadingFiles: (customerId: string, siteId: string): boolean => {
    const req = get().getGcpRequest(customerId, siteId);
    return Boolean(req && (req.measurementUploads.some((u) => !u.isDone) || req.photoUploads.some((u) => !u.isDone)));
  },

  bytesPerSecond: undefined,
  setBytesPerSecond: (bytesPerSecond) => {
    set({ bytesPerSecond });
  },

  fileRegistrationTimeMs: InitialFileRegistrationTimeMs,
  setFileRegistrationTimeMs: (fileRegistrationTimeMs) => {
    set({ fileRegistrationTimeMs });
  },
});

export const useUploadStore = create<UploadStoreState>()(
  devtools(createUploadStoreImpl, {
    name: 'UploadStore',
    enabled: !isProduction,
  }),
);
